import React, { useState } from 'react'
import type { SetupClient } from '../../../api/setupClient'
import {
  StepContainer,
  StepHeading,
  StepDescription,
  ErrorAlert,
  ButtonRow,
  PrimaryButton,
  SecondaryButton,
} from '../setupStyled'

interface FinishProps {
  client: SetupClient
  serviceName: string
  onComplete: () => void
  onBack: () => void
}

/**
 * Sixth and final step: call `complete()` to close the bootstrap window
 * (server-side this consumes the setup token — see init_server.go's
 * `initComplete` — so it can never be replayed) and hand off to the caller
 * (`SetupWizard`) to redirect to `/admin`. A refusal here (e.g. the server's
 * `ErrInitPhishingResistantFactorRequired`, in case client-side state ever
 * drifted from server state) is shown inline rather than silently
 * navigating away, so the operator understands why the wizard is not done.
 *
 * OIDCPAM Gate-6 Phase 2b: migrated off MUI onto a minimal local themed
 * content step (see setupStyled.ts) — the complete()/onComplete wiring is
 * unchanged from the pre-migration version.
 */
export const Finish = ({
  client,
  serviceName,
  onComplete,
  onBack,
}: FinishProps): JSX.Element => {
  const [status, setStatus] = useState<'idle' | 'working' | 'error'>('idle')
  const [error, setError] = useState<string | null>(null)

  const handleFinish = async (): Promise<void> => {
    setStatus('working')
    setError(null)
    try {
      await client.complete()
      onComplete()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to complete setup')
      setStatus('error')
    }
  }

  return (
    <StepContainer>
      <StepHeading>Finish</StepHeading>
      <StepDescription>
        {serviceName ? `${serviceName} is` : 'Your deployment is'} ready.
        Finishing setup closes the one-time setup token so it can never be used
        again.
      </StepDescription>
      {status === 'error' && error && <ErrorAlert role="alert">{error}</ErrorAlert>}
      <ButtonRow>
        <SecondaryButton
          type="button"
          onClick={onBack}
          disabled={status === 'working'}
        >
          Back
        </SecondaryButton>
        <PrimaryButton
          type="button"
          onClick={() => handleFinish()}
          disabled={status === 'working'}
          aria-busy={status === 'working' || undefined}
        >
          {status === 'working' ? 'Finishing…' : 'Finish setup'}
        </PrimaryButton>
      </ButtonRow>
    </StepContainer>
  )
}
